import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Pagination, Empty, Button, Modal, Upload, Tag } from 'antd';
import {
  FileTextOutlined,
  DownloadOutlined,
  EyeOutlined,
  WalletOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import CurrencyFormat from 'react-currency-format';
import { useSearchParams } from 'react-router-dom';
import lib from '../../../assets/utils/lib';
import { notification } from 'antd';
import { useAuth } from '../../../assets/utils/useAuth';
import {
  getClaimsArchiveScope,
  withClaimsArchiveScope,
  canManageClaimsArchive,
  buildClaimsArchiveRequest,
} from '../claimsArchiveHelpers';
import {
  getClaimsApprovedTotal,
  getPaApprovedTotal,
  getSubmittedTotal,
} from '../claimFinancialHelpers';
import ArchiveClaimsModal from '../ArchiveClaimsModal';
import dayjs from 'dayjs';
import { uri } from '../../../assets/utils/http-request';
import PaymentClaimTableRow from './PaymentClaimTableRow';
import { isPaymentAdvicePending, PAID_ADVICE_PENDING_LABEL } from './paymentAdvice';
import { usePermissions } from '../../../assets/utils/usePermissions';
import './PaymentClaims.css';
import './PaymentDrilldown.css';

const PAID_SEGMENT = 3;

function getMonthKey(month) {
  if (!month) return null;
  if (typeof month === 'string') return month;
  return month.month || month.key || null;
}

function formatMonthLabel(month) {
  const key = getMonthKey(month);
  if (!key) return 'Month';
  return dayjs(`${key}-01`).format('MMMM YYYY');
}

function ClaimRowSkeleton() {
  return (
    <div className="drill-row-skeleton" aria-hidden>
      <div className="drill-row-skeleton__block" style={{ width: 32, height: 32, borderRadius: 8 }} />
      <div className="drill-row-skeleton__block" style={{ width: '32%' }} />
      <div className="drill-row-skeleton__block" style={{ width: 70, marginLeft: 'auto' }} />
      <div className="drill-row-skeleton__block" style={{ width: 90 }} />
    </div>
  );
}

/**
 * PaymentBatchClaims — claims within a specific payment batch (Paid status).
 */
const PaymentBatchClaims = ({ month, facilityId, paymentBatch, facilityName, onClaimClick }) => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const canArchiveClaims = canManageClaimsArchive(user);
  const { hasAnyPermissionWithLegacy } = usePermissions();
  // Same people who can mark claims Paid can add the advice afterwards.
  const canRecordPayment = hasAnyPermissionWithLegacy(['claims.payment', 'payments.create'], [5]);
  const [adviceModalOpen, setAdviceModalOpen] = useState(false);
  const [adviceFile, setAdviceFile] = useState(null);
  const [adviceUploading, setAdviceUploading] = useState(false);
  const archiveScope = getClaimsArchiveScope(searchParams);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveModalMode, setArchiveModalMode] = useState('archive');
  const [archiveRequest, setArchiveRequest] = useState(null);
  const [claims, setClaims] = useState([]);
  const [batchInfo, setBatchInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentDocVisible, setPaymentDocVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const fetchingRef = useRef(false);

  const monthLabel = formatMonthLabel(month);
  const displayFacilityName = facilityName || 'Facility';

  const fetchBatchClaims = useCallback(async (page = 1) => {
    if (!month || !facilityId || !paymentBatch) return;
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    const monthKey = getMonthKey(month);
    if (!monthKey) {
      notification.error({ message: 'Error', description: 'Invalid month format' });
      fetchingRef.current = false;
      return;
    }

    setLoading(true);
    try {
      const response = await lib.getPaymentBatchClaims(monthKey, facilityId, paymentBatch, withClaimsArchiveScope({
        page,
        pageSize: pagination.pageSize,
        sortBy: 'date_paid',
        sortOrder: 'desc',
      }, archiveScope));

      if (response?.data?.error) {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to fetch batch claims',
        });
        return;
      }

      const responseData = response?.data || {};
      const claimsList = Array.isArray(responseData.data) ? responseData.data : [];

      setClaims(claimsList);
      setBatchInfo(responseData.batch_info || null);
      setPagination((prev) => ({
        ...prev,
        current: responseData.pagination?.page || 1,
        pageSize: responseData.pagination?.pageSize || prev.pageSize,
        total: responseData.pagination?.total || claimsList.length || 0,
      }));
    } catch (error) {
      console.error('Error fetching batch claims:', error);
      notification.error({ message: 'Error', description: 'Failed to fetch batch claims' });
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [month, facilityId, paymentBatch, pagination.pageSize, archiveScope]);

  useEffect(() => {
    if (month && facilityId && paymentBatch) {
      fetchBatchClaims(1);
    }
  }, [month, facilityId, paymentBatch, fetchBatchClaims]);

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
    fetchBatchClaims(page);
  };

  const getClaimKey = (claim) => claim.claims_id || claim.checkin_id;

  const advicePending = isPaymentAdvicePending(batchInfo);

  const uploadAdvice = async () => {
    if (!adviceFile) return;
    setAdviceUploading(true);
    try {
      const res = await lib.uploadPaymentAdvice(paymentBatch, adviceFile);
      const body = res?.data ?? res;
      if (res?.error ?? body?.error) {
        notification.error({ message: 'Upload failed', description: res?.message ?? body?.message, placement: 'topLeft' });
      } else {
        notification.success({
          message: 'Payment advice uploaded',
          description: 'The claims in this batch now show as Paid.',
          placement: 'topLeft',
        });
        setAdviceModalOpen(false);
        setAdviceFile(null);
        fetchBatchClaims(pagination.current);
      }
    } finally {
      setAdviceUploading(false);
    }
  };

  const getPaymentDocumentUrl = () => {
    if (!batchInfo?.payment_batch || isPaymentAdvicePending(batchInfo)) return null;
    const fileName = batchInfo.payment_evidence_file || `${batchInfo.payment_batch}.pdf`;
    return `${uri}documents/claims/payments/claims/${fileName}`;
  };

  if (!month || !facilityId || !paymentBatch) {
    return <Empty description="Missing required parameters" />;
  }

  const claimCountLabel = loading
    ? 'Loading…'
    : `${pagination.total.toLocaleString()} claim${pagination.total === 1 ? '' : 's'}`;
  const batchSubmitted = getSubmittedTotal(batchInfo);
  const batchPaApproved = getPaApprovedTotal(batchInfo);
  const batchApproved = batchInfo ? getClaimsApprovedTotal(batchInfo) : null;
  const batchPaidDate = batchInfo?.date_paid
    ? dayjs(batchInfo.date_paid).format('MMM D, YYYY')
    : null;
  const docUrl = getPaymentDocumentUrl();
  const hasBatchMoney = batchInfo != null;

  return (
    <div className="payment-claims payment-batch-claims">
      <div className="drill-panel">
        <div className="drill-panel__head">
          <div>
            <h2>
              {displayFacilityName}
              <span className="payment-claims-header__pill payment-claims-header__pill--batch" style={{ marginLeft: 8 }} title={paymentBatch}>
                <WalletOutlined aria-hidden /> {paymentBatch}
              </span>
            </h2>
            <p>
              {advicePending ? (
                <Tag color="orange" style={{ marginInlineEnd: 6 }}>{PAID_ADVICE_PENDING_LABEL}</Tag>
              ) : 'Paid · '}
              {monthLabel} · {claimCountLabel}
              {hasBatchMoney ? (
                <>
                  {' · Submitted '}<CurrencyFormat value={batchSubmitted} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                  {' · PA approved '}<CurrencyFormat value={batchPaApproved} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                  {' · Claims approved '}<CurrencyFormat value={batchApproved} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                </>
              ) : null}
              {batchPaidDate ? ` · Paid ${batchPaidDate}` : null}
            </p>
          </div>
          <div className="payment-claims-toolbar__actions">
            {docUrl ? (
              <Button icon={<EyeOutlined />} onClick={() => setPaymentDocVisible(true)} size="small">
                Payment doc
              </Button>
            ) : null}
            {advicePending && canRecordPayment ? (
              <Button type="primary" icon={<UploadOutlined />} onClick={() => setAdviceModalOpen(true)} size="small">
                Upload payment advice
              </Button>
            ) : null}
            {canArchiveClaims ? (
              <Button
                size="small"
                onClick={() => {
                  const monthKey = getMonthKey(month);
                  setArchiveRequest(buildClaimsArchiveRequest({
                    scope: 'facility',
                    month: monthKey,
                    facilityId,
                    // This view is the Paid queue only; archiving stays there.
                    segment: PAID_SEGMENT,
                  }));
                  setArchiveModalMode(archiveScope === 'archived' ? 'unarchive' : 'archive');
                  setArchiveModalOpen(true);
                }}
              >
                {archiveScope === 'archived' ? 'Unarchive this month' : 'Archive this month'}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="payment-claims-body">
          {loading ? (
            <div aria-label="Loading batch claims">
              {Array.from({ length: 6 }).map((_, i) => (
                <ClaimRowSkeleton key={`skel-${i}`} />
              ))}
            </div>
          ) : claims.length === 0 ? (
            <div className="claims-empty">
              <FileTextOutlined style={{ fontSize: 28, color: '#94a3b8' }} />
              <p className="claims-empty__title">No claims in this batch</p>
              <p className="claims-empty__desc">
                Batch {paymentBatch} has no claims for {displayFacilityName}.
              </p>
            </div>
          ) : (
            <div className="claims-table-wrap">
              <table className="claims-drill-table" aria-label="Batch claims">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Encounter</th>
                    <th className="num">Submitted</th>
                    <th className="num">PA approved</th>
                    <th className="num">Payable</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map((claim) => {
                    const claimKey = getClaimKey(claim);
                    return (
                      <PaymentClaimTableRow
                        key={claimKey}
                        claim={claim}
                        segment={PAID_SEGMENT}
                        selectionMode={false}
                        isSelected={false}
                        onClick={() => onClaimClick?.(claim)}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && pagination.total > pagination.pageSize ? (
            <div className="payment-claims-pagination">
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={handlePageChange}
                showSizeChanger={false}
              />
            </div>
          ) : null}
        </div>
      </div>

      <Modal
        title={
          <span>
            <FileTextOutlined /> Payment document
          </span>
        }
        open={paymentDocVisible}
        onCancel={() => setPaymentDocVisible(false)}
        footer={[
          <Button
            key="download"
            icon={<DownloadOutlined />}
            onClick={() => docUrl && window.open(docUrl, '_blank')}
          >
            Download
          </Button>,
          <Button key="close" onClick={() => setPaymentDocVisible(false)}>
            Close
          </Button>,
        ]}
        width="90%"
        style={{ top: 20 }}
        bodyStyle={{ padding: 0, height: 'calc(100vh - 200px)', overflow: 'hidden' }}
      >
        {docUrl ? (
          <iframe
            src={docUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="Payment document"
          />
        ) : null}
      </Modal>

      <Modal
        title="Upload payment advice"
        open={adviceModalOpen}
        onCancel={() => { setAdviceModalOpen(false); setAdviceFile(null); }}
        onOk={uploadAdvice}
        okText="Upload"
        okButtonProps={{ disabled: !adviceFile, loading: adviceUploading }}
        destroyOnClose
      >
        <p style={{ marginTop: 0 }}>
          This batch was marked Paid without its payment advice. Uploading it here attaches it to every claim in the
          batch, and they show as Paid.
        </p>
        <Upload
          accept=".pdf,application/pdf"
          maxCount={1}
          beforeUpload={(file) => {
            const ok = /\.pdf$/i.test(file.name || '') || file.type === 'application/pdf';
            if (!ok) {
              notification.error({ message: 'Invalid file type', description: 'Allowed file type: PDF', placement: 'topLeft' });
              return Upload.LIST_IGNORE;
            }
            if (file.size > 10 * 1024 * 1024) {
              notification.error({ message: 'File too large', description: 'File size must not exceed 10MB', placement: 'topLeft' });
              return Upload.LIST_IGNORE;
            }
            setAdviceFile(file);
            return false;
          }}
          onRemove={() => setAdviceFile(null)}
        >
          <Button icon={<UploadOutlined />}>Select PDF</Button>
        </Upload>
      </Modal>

      <ArchiveClaimsModal
        open={archiveModalOpen}
        mode={archiveModalMode}
        request={archiveRequest}
        segment={PAID_SEGMENT}
        onClose={() => {
          setArchiveModalOpen(false);
          setArchiveRequest(null);
        }}
        onSuccess={() => {
          setArchiveModalOpen(false);
          setArchiveRequest(null);
          fetchBatchClaims(pagination.current);
        }}
      />
    </div>
  );
};

export default PaymentBatchClaims;
